"use client";

import { useAutoSave } from "@/hooks/useAutoSave";
import type { FormField } from "@/lib/database/forms";
import { createClient } from "@/utils/supabase/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DRAFT_FIELD_TYPES_KEY, FORM_DRAFT_AUTOSAVE_DELAY_MS } from "../_logic/form-draft-constants";
import { deleteFormDraft, getFormDraft, migrateLocalDraftToDb, saveFormDraft } from "../_logic/form-draft-actions";
import { getFieldTypesByLabel, getSensitiveDraftKeys } from "../_logic/form-field-utils";
import { dropStaleTypedDraftValues } from "../_logic/drop-stale-draft-values";
import { clearLocalFormDraft, readLocalFormDraft, saveLocalFormDraft } from "../_logic/form-draft-storage";

/**
 * Merges a field-type snapshot into a draft payload before it's persisted (see
 * `dropStaleTypedDraftValues` for why). Bounded to the labels actually present in `data` so
 * the snapshot doesn't inflate every save with types for fields the respondent never touched.
 * Guarded against the case of a real field labeled exactly the reserved key — nothing
 * validates field labels against it, so it's reachable, not theoretical — by skipping the
 * stamp entirely for that one save rather than risk silently clobbering that field's answer.
 *
 * @param data - The draft's current answers, keyed by label.
 * @param fieldTypesByLabel - The form's current field types, keyed by label.
 * @returns `data` with the type snapshot merged in, or `data` unchanged on a reserved-key collision.
 */
function stampFieldTypes(
  data: Record<string, unknown>,
  fieldTypesByLabel: Record<string, string>
): Record<string, unknown> {
  if (DRAFT_FIELD_TYPES_KEY in data) return data;

  const relevantTypes: Record<string, string> = {};
  for (const label of Object.keys(data)) {
    const type = fieldTypesByLabel[label];
    if (type !== undefined) relevantTypes[label] = type;
  }

  return { ...data, [DRAFT_FIELD_TYPES_KEY]: relevantTypes };
}

interface UseFormAutoSaveOptions {
  formId: number;
  eventId?: number | null;
  /** Landing page the form is open on — lets drafts honor the page's inherited SSO grant. */
  landingPageId?: number | null;
  formData: Record<string, unknown>;
  /**
   * Field definitions — used to redact phone/email PII from anonymous localStorage drafts,
   * and to stamp/verify each field's type so a restored draft can't apply a stale value under
   * a field's new type after the builder changes it (see `dropStaleTypedDraftValues`).
   */
  formFields?: FormField[];
  currentPage?: number;
  delay?: number;
  onDraftRestored?: (draftData: Record<string, unknown>, currentPage: number) => void;
  disabled?: boolean;
  hasExistingResponse?: boolean;
}

interface UseFormAutoSaveReturn {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  saveNow: () => Promise<boolean>;
  clearDraft: () => Promise<void>;
  isAuthenticated: boolean;
  isRestoring: boolean;
}

/**
 * Autosave hook for forms. Saves to the DB for authenticated users, localStorage for anonymous.
 * Skips invalid form IDs and never autosaves during draft restoration. Listener callbacks read
 * from refs so re-renders triggered by typing don't tear down `beforeunload`/`visibilitychange`.
 * @returns Autosave state and controls
 */
export function useFormAutoSave({
  formId,
  eventId,
  landingPageId,
  formData,
  formFields,
  currentPage = 0,
  delay = FORM_DRAFT_AUTOSAVE_DELAY_MS,
  onDraftRestored,
  disabled = false,
  hasExistingResponse = false,
}: UseFormAutoSaveOptions): UseFormAutoSaveReturn {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);
  const restoredRef = useRef(false);
  const prevDisabledRef = useRef(disabled);
  const isAuthenticatedRef = useRef<boolean | null>(null);
  const isRestoringRef = useRef(true);
  const supabase = useMemo(() => createClient(), []);

  // Refs the listeners and the stable handleSave callback read from, so we don't recreate them per keystroke.
  const formDataRef = useRef(formData);
  const currentPageRef = useRef(currentPage);
  const formIdRef = useRef(formId);
  const eventIdRef = useRef(eventId);
  const landingPageIdRef = useRef(landingPageId);
  const disabledRef = useRef(disabled);
  const onDraftRestoredRef = useRef(onDraftRestored);

  // Labels of phone/email fields, redacted from anonymous localStorage drafts so
  // PII isn't persisted as clear text. Held in a ref so the stable save callbacks
  // and unload listeners read the latest set without being recreated per render.
  const sensitiveKeys = useMemo(() => getSensitiveDraftKeys(formFields ?? []), [formFields]);
  const sensitiveKeysRef = useRef(sensitiveKeys);
  useEffect(() => {
    sensitiveKeysRef.current = sensitiveKeys;
  }, [sensitiveKeys]);

  // Current field types by label — stamped into every save (see DRAFT_FIELD_TYPES_KEY) and
  // compared against on restore to drop a value whose field has since changed type.
  const fieldTypesByLabel = useMemo(() => getFieldTypesByLabel(formFields ?? []), [formFields]);
  const fieldTypesByLabelRef = useRef(fieldTypesByLabel);
  useEffect(() => {
    fieldTypesByLabelRef.current = fieldTypesByLabel;
  }, [fieldTypesByLabel]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);
  useEffect(() => {
    formIdRef.current = formId;
  }, [formId]);
  useEffect(() => {
    eventIdRef.current = eventId;
  }, [eventId]);
  useEffect(() => {
    landingPageIdRef.current = landingPageId;
  }, [landingPageId]);
  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);
  useEffect(() => {
    onDraftRestoredRef.current = onDraftRestored;
  }, [onDraftRestored]);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  useEffect(() => {
    isRestoringRef.current = isRestoring;
  }, [isRestoring]);

  // Skip autosave for invalid form IDs (Number.isInteger guards against parseInt("abc") = NaN, etc.)
  const formIdValid = Number.isInteger(formId) && formId > 0;

  // Reset restoration state when the modal reopens.
  useEffect(() => {
    if (prevDisabledRef.current && !disabled) {
      restoredRef.current = false;
      setIsRestoring(true);
    }
    prevDisabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    if (!formIdValid) return;
    let mounted = true;

    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (mounted) setIsAuthenticated(!!session);
    };

    checkAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      const wasAuthenticated = isAuthenticatedRef.current;
      const nowAuthenticated = !!session;
      setIsAuthenticated(nowAuthenticated);

      if (event === "SIGNED_IN" && !wasAuthenticated && nowAuthenticated) {
        const localDraft = readLocalFormDraft(formIdRef.current, eventIdRef.current);
        if (localDraft) {
          const result = await migrateLocalDraftToDb(
            formIdRef.current,
            localDraft,
            eventIdRef.current,
            landingPageIdRef.current
          );
          if (result.success) {
            clearLocalFormDraft(formIdRef.current, eventIdRef.current);
          }
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, formIdValid]);

  // Restore once per mount (or per reopen), reading from refs so this effect doesn't re-run per keystroke.
  useEffect(() => {
    if (!formIdValid) {
      setIsRestoring(false);
      return;
    }
    if (hasExistingResponse) {
      setIsRestoring(false);
      return;
    }
    if (isAuthenticated === null || disabled || restoredRef.current) return;

    const restoreDraft = async () => {
      restoredRef.current = true;

      try {
        if (isAuthenticated) {
          const result = await getFormDraft(formIdRef.current, landingPageIdRef.current);
          if (result?.success && result.data && onDraftRestoredRef.current) {
            const draftData = dropStaleTypedDraftValues(result.data.draftData, fieldTypesByLabelRef.current);
            onDraftRestoredRef.current(draftData, result.data.currentPage);
          }
        } else {
          const localDraft = readLocalFormDraft(formIdRef.current, eventIdRef.current);
          if (localDraft && onDraftRestoredRef.current) {
            const draftData = dropStaleTypedDraftValues(localDraft.draftData, fieldTypesByLabelRef.current);
            onDraftRestoredRef.current(draftData, localDraft.currentPage);
          }
        }
      } catch (error) {
        console.error("useFormAutoSave: restore", { formId: formIdRef.current }, error);
      } finally {
        setIsRestoring(false);
      }
    };

    restoreDraft();
  }, [isAuthenticated, disabled, hasExistingResponse, formIdValid]);

  // Stable: reads from refs. useAutoSave never rebuilds this callback per keystroke.
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (disabledRef.current || isRestoringRef.current) return false;
    const authState = isAuthenticatedRef.current;
    if (authState === null) return false;
    if (!Number.isInteger(formIdRef.current) || formIdRef.current <= 0) return false;

    // Added here rather than inside saveLocalFormDraft so it survives that function's PII
    // redaction untouched — the reserved key is never a real field label, so it's never in
    // `redactedKeys`.
    const dataWithFieldTypes = stampFieldTypes(formDataRef.current, fieldTypesByLabelRef.current);

    try {
      if (authState) {
        const result = await saveFormDraft(
          formIdRef.current,
          dataWithFieldTypes,
          currentPageRef.current,
          eventIdRef.current,
          landingPageIdRef.current
        );
        return result.success;
      }
      saveLocalFormDraft(
        formIdRef.current,
        dataWithFieldTypes,
        currentPageRef.current,
        eventIdRef.current,
        sensitiveKeysRef.current
      );
      return true;
    } catch (error) {
      // A draft save that's aborted mid-flight (navigation away) or fails on a
      // transient network blip isn't a real error — the next tick retries and no
      // data is lost. Don't surface those as console errors; only log real ones.
      const isTransient =
        (error instanceof DOMException && error.name === "AbortError") ||
        (error instanceof TypeError && /networkerror|failed to fetch|load failed/i.test(error.message));
      if (!isTransient) {
        console.error("useFormAutoSave: save", { formId: formIdRef.current }, error);
      }
      return false;
    }
  }, []);

  const { isSaving, lastSaved, hasUnsavedChanges, saveNow } = useAutoSave({
    delay,
    onSave: handleSave,
    // Skip change detection while restoring so restored data isn't re-saved 500ms later.
    dependencies: [formIdValid && !isRestoring ? formData : null, currentPage],
  });

  const saveNowRef = useRef(saveNow);
  useEffect(() => {
    saveNowRef.current = saveNow;
  }, [saveNow]);

  const clearDraft = useCallback(async () => {
    if (!Number.isInteger(formId) || formId <= 0) return;
    clearLocalFormDraft(formId, eventId);
    if (isAuthenticated) {
      await deleteFormDraft(formId, landingPageId);
    }
  }, [formId, eventId, landingPageId, isAuthenticated]);

  // Listeners registered once. They read live state through refs.
  useEffect(() => {
    if (!formIdValid) return;

    const handleBeforeUnload = () => {
      if (disabledRef.current) return;
      // beforeunload can't reliably await a server action, but a synchronous localStorage
      // write succeeds. Acts as a last-ditch fallback for the in-flight debounce window.
      // Stamped the same as a regular save (stampFieldTypes) — this path writes just as
      // durably to localStorage, so it needs the same type snapshot for dropStaleTypedDraftValues
      // to protect a draft that happened to be flushed here instead of by the debounce.
      saveLocalFormDraft(
        formIdRef.current,
        stampFieldTypes(formDataRef.current, fieldTypesByLabelRef.current),
        currentPageRef.current,
        eventIdRef.current,
        sensitiveKeysRef.current
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return;
      if (disabledRef.current) return;
      saveNowRef.current();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [formIdValid]);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    saveNow,
    clearDraft,
    isAuthenticated: isAuthenticated ?? false,
    isRestoring,
  };
}
